/**
 * Creative Finance solution page — Sub-To, Seller-Finance, Novation.
 */
import { ScrollText, Handshake, Brain, Target, Users2, ShieldCheck } from "lucide-react";
import SolutionPageTemplate from "../SolutionPageTemplate";

export default function CreativeFinanceSolutionPage() {
  return (
    <SolutionPageTemplate
      slug="creative-finance"
      persona="Sub-To, Seller-Finance, and Novation operators"
      eyebrow="Solutions · Creative Finance"
      title="Deeper margins."
      titleAccent="Structured on evidence."
      subtitle="Subject-To, Seller-Finance, and Novation aren't gut-feel deals — they're structural. iSpeedToClose scores every creative exit against the wholesale and flip alternatives with a numeric Why-Not audit."
      bullets={[
        "Sub-To, Seller-Finance, Novation, Wholetail all scored in parallel (BP-014)",
        "Coach explains creative structure with cited evidence and confidence caps",
        "Contract clause library + timeline audit built for lender + attorney review",
      ]}
      metricStrip={[
        { stat: "+$41k", label: "avg. EV lift vs wholesale" },
        { stat: "8", label: "exit strategies scored" },
        { stat: "82%", label: "avg. Coach confidence" },
        { stat: "100%", label: "actions on timeline" },
      ]}
      pains={[
        { title: "Team debates structure, not deal", desc: "'Should we novate or Sub-To?' Endless meetings. No numeric answer. Deal dies waiting." },
        { title: "Sellers get cold feet mid-deal", desc: "You promised numbers you couldn't cite. When the seller's attorney asks 'why', the deal collapses." },
        { title: "Missing DD on payoffs & liens", desc: "Sub-To without a clean payoff verification is a lawsuit waiting to happen." },
        { title: "Buyer & lender need proof", desc: "Novation buyers demand a paper trail. Without an audit log, funding falls through." },
      ]}
      playbook={[
        { step: 1, title: "Score structures", desc: "Sub-To, Novation, Seller-Finance, Wholetail all ranked side-by-side.", bp: "BP-014" },
        { step: 2, title: "Cite evidence", desc: "Coach synthesises with confidence cap when data is thin.", bp: "BP-025" },
        { step: 3, title: "Execute cleanly", desc: "Contract clause library + attorney-ready timeline audit.", bp: "BP-002 · BP-013" },
        { step: 4, title: "Match buyers/lenders", desc: "Ranked buyers who close creative deals — not first-time flippers.", bp: "BP-022" },
      ]}
      productsUsed={[
        { icon: Target, title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Every creative structure scored numerically with a Why-Not audit." },
        { icon: Brain, title: "AI Acquisition Coach", to: "/products/ai-acquisition-coach", desc: "Grounded, cited recommendations — never invented numbers on creative deals." },
        { icon: ScrollText, title: "Property Workspace", to: "/products/property-workspace", desc: "Immutable timeline for every clause, signer, and payoff verification." },
        { icon: Users2, title: "Buyer Intelligence", to: "/products/buyer-intelligence", desc: "Filter by 'creative-finance friendly' — Sub-To and novation buyers first." },
        { icon: Handshake, title: "Portfolio Intelligence", to: "/products/portfolio-intelligence", desc: "Track Sub-To acquisitions with their underlying financing status." },
        { icon: ShieldCheck, title: "Platform Governance", to: "/platform-overview", desc: "MFA + audit trails + retention — creative deals demand governance." },
      ]}
      proof={{
        stat: "+$41k",
        label: "avg. EV over wholesale on Sub-To deals",
        quote: "We stopped defaulting to wholesale. The Why-Not panel showed us Sub-To was worth another $41k on the exact same property.",
        attribution: "Creative-finance operator · Ohio",
      }}
      related={[
        { title: "Wholesalers", to: "/solutions/wholesalers", desc: "Fast-cash assignments when structure doesn't pencil." },
        { title: "Buy & Hold", to: "/solutions/buy-and-hold", desc: "Where Sub-To acquisitions end up long-term." },
        { title: "Teams & Enterprise", to: "/solutions/teams-enterprise", desc: "Governance built for creative-deal audit reviews." },
      ]}
    />
  );
}
